import useShopifySubscription from 'hooks/billing/useShopifySubscription';
import {
    AppInstallationSubscription,
    AppRecurringPricingDetails,
} from 'hooks/shopify/queries/get-all-app-subscriptions.graphql';
import { SubscriptionStatus } from 'models/SubscriptionStatus';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';
import {
    selectShopifyAppHandle,
    selectShopifyAppPlans,
    selectShopifyAppPlansFetching,
    selectShopifySubscriptionFetching,
} from 'redux/slices/shopify-app-pricing/shopify-app-pricing.selector';
import { Addon } from '../../../pb/subscription/v1/addon_pb';

interface CurrentSubscriptionHelper {
    appHandle: string | undefined;
    currentSubscription: AppInstallationSubscription | undefined;
    isLoading: boolean;
    isLegacy: boolean;
    isCancelled: boolean;
    addons: Addon[];
    planHandle: string | undefined;
    goToPLans: () => void;
    goToBilling: () => void;
}

const useCurrentSubscriptionHelper = (): CurrentSubscriptionHelper => {
    const shopifyPlans = useAppSelector(selectShopifyAppPlans);
    const appHandle = useAppSelector(selectShopifyAppHandle);
    const plansFetching = useAppSelector(selectShopifyAppPlansFetching);
    const subscriptionFetching = useAppSelector(selectShopifySubscriptionFetching);
    const { currentSubscription } = useShopifySubscription();
    const isLoading = useMemo(() => plansFetching || subscriptionFetching, [plansFetching, subscriptionFetching]);

    const planHandle = useMemo(() => {
        return (currentSubscription?.lineItems?.[0]?.plan?.pricingDetails as AppRecurringPricingDetails)?.planHandle;
    }, [currentSubscription]);

    const currentPlanDetails = useMemo(() => {
        return shopifyPlans?.plans.find((p) => p.handle === planHandle);
    }, [planHandle, shopifyPlans]);

    const isLegacy = useMemo(() => {
        return !(currentSubscription?.lineItems?.[0]?.plan?.pricingDetails as AppRecurringPricingDetails)?.planHandle;
    }, [currentSubscription]);

    const isCancelled = useMemo(() => {
        return currentSubscription?.status === SubscriptionStatus.CANCELLED;
    }, [currentSubscription]);

    const addons = useMemo(() => {
        return currentPlanDetails?.addons || [];
    }, [currentPlanDetails]);

    const goToPLans = useCallback(
        () => open(`shopify://admin/charges/${appHandle}/pricing_plans`, '_top'),
        [appHandle]
    );

    const goToBilling = useCallback(
        () => open(`shopify://admin/settings/apps/app_installations/app/${appHandle}`, '_top'),
        [appHandle]
    );

    return {
        appHandle,
        currentSubscription,
        isLoading,
        isLegacy,
        isCancelled,
        addons,
        planHandle,
        goToPLans,
        goToBilling,
    };
};

export default useCurrentSubscriptionHelper;
